import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { UserDateFormat } from '../../../../../../components/user-date-format/index';
import { DataDrivenLogo } from '../../../../../../components/data-driven-logo';
import Global from '../../../../../../store/global-selectors';
import { useManageSubscriptionDrawerContext } from '../../../context/manage-subscription-drawer-context';
import { useNonWalletedAndNonExcludedSellerLabelProviders } from '../../../../../../hooks/use-seller-label-providers';
import { useGetPlanPrice, useSelectedPlan } from '../../../hooks/use-plan-details';
import { getProviderDisplayName } from '../../../../../../components/data-driven-logo/get-provider-display-name';
import { useIsTrialWithByoaProviders } from '../../../../../../hooks/non-wallet-access-hooks';
import { isBillingPlanIdUsageBilledHighVolume } from '../../../../../../enums/billing-plan-ids';

import { messages } from './messages';
import style from './style.module.css';
import { emptyArray } from 'ui-components/default-props/empty';

const WarningMessage = ({
  isUpgradingButLosingNonWalletAccess = false,
  isDowngradingToPlanWithoutNonWalletAccess = false,
  byoaSellerLabelProviders = emptyArray,
  pendingDateTimeUTC = '',
  endDateTimeUTC = '',
}) => {
  if (isUpgradingButLosingNonWalletAccess) {
    return (
      <InlineMessage className={style['deactivation-message']} type={INLINE_MESSAGE_TYPES.info}>
        <FormattedMessage {...messages.warningMessage} />
      </InlineMessage>
    );
  }
  if (isDowngradingToPlanWithoutNonWalletAccess) {
    return (
      <InlineMessage className={style['deactivation-message']} type={INLINE_MESSAGE_TYPES.info}>
        <FormattedMessage
          {...messages.deactivationMessageForCarrierList}
          values={{
            numberOfProviders: byoaSellerLabelProviders?.length,
            date: <UserDateFormat value={pendingDateTimeUTC ?? endDateTimeUTC} />,
          }}
        />
      </InlineMessage>
    );
  }
};

WarningMessage.propTypes = {
  byoaSellerLabelProviders: PropTypes.array,
  isDowngradingToPlanWithoutNonWalletAccess: PropTypes.bool,
  isUpgradingButLosingNonWalletAccess: PropTypes.bool,
  pendingDateTimeUTC: PropTypes.string,
  endDateTimeUTC: PropTypes.string,
};

const GenericWarningMessage = ({
  isUpgradingButLosingNonWalletAccess = false,
  isDowngradingToPlanWithoutNonWalletAccess = false,
  pendingDateTimeUTC = '',
  endDateTimeUTC = '',
}) => {
  if (isUpgradingButLosingNonWalletAccess) {
    return (
      <InlineMessage className={style['deactivation-message-no-carriers']} type={INLINE_MESSAGE_TYPES.info}>
        <FormattedMessage {...messages.warningMessage} />
      </InlineMessage>
    );
  }
  if (isDowngradingToPlanWithoutNonWalletAccess) {
    return (
      <InlineMessage className={style['deactivation-message-no-carriers']} type={INLINE_MESSAGE_TYPES.info}>
        <FormattedMessage
          {...messages.deactivationMessage}
          values={{
            date: <UserDateFormat value={pendingDateTimeUTC ?? endDateTimeUTC} />,
          }}
        />
      </InlineMessage>
    );
  }
};

GenericWarningMessage.propTypes = {
  isDowngradingToPlanWithoutNonWalletAccess: PropTypes.bool,
  isUpgradingButLosingNonWalletAccess: PropTypes.bool,
  pendingDateTimeUTC: PropTypes.string,
  endDateTimeUTC: PropTypes.string,
};

export const DeactivationMessage = React.memo(() => {
  const { byoaEnabled } = useManageSubscriptionDrawerContext();
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);

  const { currentBillingPlan = {}, planToNonWalletAccessPlanMap = {} } = subscriptionData ?? {};
  const { pendingDateTimeUTC, endDateTimeUTC } = planInfo ?? {};

  const selectedPlan = useSelectedPlan();
  const [, sellerByoaPlan] =
    Object.entries(planToNonWalletAccessPlanMap).find(([, { billingPlanId }]) => currentBillingPlan?.billingPlanId === billingPlanId) ?? [];
  const selectedPlanPrice = useGetPlanPrice(selectedPlan);
  const currentPlanPrice = useGetPlanPrice(currentBillingPlan);
  // TODO: I don't like that sometimes sellerBYOAPlan is undefined and we _have_ to call this hook unconditionally
  const currentByoaPlanPrice = useGetPlanPrice(sellerByoaPlan);

  const isDowngradingFromHighVolume =
    selectedPlanPrice.value <= currentPlanPrice.value && isBillingPlanIdUsageBilledHighVolume(currentBillingPlan?.billingPlanId);
  const isDowngradingFromPlanWithNonWalletAcces = selectedPlanPrice.value <= currentByoaPlanPrice.value;
  const isDowngradingToPlanWithoutNonWalletAccess =
    (isDowngradingFromHighVolume || isDowngradingFromPlanWithNonWalletAcces) && !byoaEnabled;

  const isHighVolumePlanSelected = isBillingPlanIdUsageBilledHighVolume(selectedPlan?.billingPlanId);
  const isInTrialWithNonWalletAccess = useIsTrialWithByoaProviders();
  const isUpgradingFromPlanWithNonWalletAccess = sellerByoaPlan && selectedPlanPrice.value > currentByoaPlanPrice.value;
  const isUpgradingButLosingNonWalletAccess =
    (isInTrialWithNonWalletAccess || isUpgradingFromPlanWithNonWalletAccess) && !byoaEnabled && !isHighVolumePlanSelected;

  const nonWalletedAndNonExcludedSellerLabelProviders = useNonWalletedAndNonExcludedSellerLabelProviders();
  const byoaSellerLabelProviders = useMemo(
    () =>
      nonWalletedAndNonExcludedSellerLabelProviders.map((sp) => {
        const providerId = Number.parseInt(sp.labelProviderId, 10);
        const nickname = sp.name ? `"${sp.name}"` : null;
        return {
          sellerLabelProviderId: sp.sellerLabelProviderId,
          nickname,
          providerId,
          displayNameMessageDescriptor: getProviderDisplayName(providerId),
        };
      }),
    [nonWalletedAndNonExcludedSellerLabelProviders],
  );
  const isLosingNonWalletAccess = isUpgradingButLosingNonWalletAccess || isDowngradingToPlanWithoutNonWalletAccess;
  if (isLosingNonWalletAccess && byoaSellerLabelProviders?.length) {
    return (
      <div className={style['byoa-loss-details']}>
        <WarningMessage
          byoaSellerLabelProviders={byoaSellerLabelProviders}
          endDateTimeUTC={endDateTimeUTC}
          isDowngradingToPlanWithoutNonWalletAccess={isDowngradingToPlanWithoutNonWalletAccess}
          isUpgradingButLosingNonWalletAccess={isUpgradingButLosingNonWalletAccess}
          pendingDateTimeUTC={pendingDateTimeUTC}
        />
        <div className={style['carriers-section']}>
          {byoaSellerLabelProviders.map(({ nickname, displayNameMessageDescriptor, providerId, sellerLabelProviderId }) => (
            <div key={sellerLabelProviderId} className={style['provider-row']}>
              <DataDrivenLogo className={style['provider-logo']} logoType='icon' providerId={providerId} />
              <div className={style['name-container']}>
                <Text
                  className={style['provider-name']}
                  textType={TextType.Paragraph}
                  value={<FormattedMessage {...displayNameMessageDescriptor} />}
                />
                {nickname}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <GenericWarningMessage
      endDateTimeUTC={endDateTimeUTC}
      isDowngradingToPlanWithoutNonWalletAccess={isDowngradingToPlanWithoutNonWalletAccess}
      isUpgradingButLosingNonWalletAccess={isUpgradingButLosingNonWalletAccess}
      pendingDateTimeUTC={pendingDateTimeUTC}
    />
  );
});
